import React from 'react';

/**
 * @file Define os estilos globais para a aplicação.
 * Este componente injeta uma tag <style> no cabeçalho do documento,
 * permitindo a aplicação de CSS global que afeta toda a aplicação,
 * incluindo a normalização de estilos de formulário, temas claro/escuro e classes utilitárias.
 */

// O componente GlobalStyles é um componente funcional sem estado que retorna
// exclusivamente uma tag <style> com todo o CSS global dentro dela.
const GlobalStyles = () => (
  <style>{`
    /* --- Estilos Base para Inputs --- */
    /* Define uma aparência consistente para todos os campos de entrada de texto. */
    .input-style {
      background-color: var(--bg-surface);
      border: 1px solid var(--border-color);
      border-radius: var(--radius-md);
      padding-top: 0.5rem; 
      padding-bottom: 0.5rem;
      box-shadow: var(--shadow-sm);
      color: var(--text-primary);
    }
    /* Adapta os inputs para o modo escuro. */
    .dark .input-style {
      background-color: var(--bg-surface);
      border-color: var(--border-color);
      color: var(--text-primary);
    }
    /* Adiciona um anel de foco para acessibilidade e feedback visual. */
    .input-style:focus {
      outline: none; 
      box-shadow: 0 0 0 2px var(--border-color-focus);
      border-color: var(--border-color-focus);
    }
    /* Estiliza os inputs quando desabilitados. */
    .input-style:disabled {
      background-color: var(--color-secondary);
      cursor: not-allowed; 
      opacity: 0.7;
    }
    .dark .input-style:disabled {
      background-color: var(--color-secondary);
    }

    /* --- Correção Visual para Select (ComboBox) --- */
    /* Remove a aparência padrão do sistema operacional para o select. */
    select.input-style {
        -webkit-appearance: none;
        -moz-appearance: none;
        appearance: none;
        /* Posiciona a seta customizada à direita. */
        background-position: right 0.5rem center;
        background-repeat: no-repeat;
        background-size: 1.5em 1.5em;
        padding-right: 2.5rem; /* Adiciona espaço para não sobrepor o texto. */
    }
    
    /* Define a seta (ícone SVG) para o modo claro. */
    select.input-style {
        background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e");
    }

    /* Define a seta (ícone SVG) para o modo escuro, com uma cor de maior contraste. */
    .dark select.input-style {
        background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%239ca3af' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e");
    }
    /* --- Fim da Correção --- */

    /* --- Botões Utilitários --- */
    .btn-admin {
        display: inline-flex; 
        align-items: center; 
        padding: 0.5rem 1rem; 
        font-size: 0.875rem; 
        font-weight: 600; 
        color: white; 
        border-radius: var(--radius-lg);
        transition: background-color 0.2s;
    }
    .btn-primary {
        padding: 0.5rem 1rem; 
        background-color: var(--color-primary);
        color: var(--color-primary-text);
        font-weight: 600; 
        border-radius: var(--radius-lg);
        transition: background-color 0.2s;
    }
    .btn-primary:hover { background-color: var(--color-primary-hover); }
    .btn-primary:disabled { background-color: #93c5fd; cursor: not-allowed; } /* Tailwind fallback */
    .dark .btn-primary:disabled { background-color: #1e40af; } /* Tailwind fallback */
    
    .btn-secondary {
        padding: 0.5rem 1rem; 
        background-color: var(--color-secondary);
        color: var(--color-secondary-text);
        font-weight: 600; 
        border-radius: var(--radius-lg);
        transition: background-color 0.2s;
    }
    .dark .btn-secondary { background-color: var(--color-secondary); color: var(--color-secondary-text); }
    .dark .btn-secondary:hover { background-color: var(--color-secondary-hover); }
    .btn-secondary:hover { background-color: var(--color-secondary-hover); }
    .btn-secondary:disabled {
      opacity: 0.7;
      cursor: not-allowed;
    }

    /* Adiciona um anel de foco para botões via teclado para acessibilidade. */
    .btn-admin:focus-visible,
    .btn-primary:focus-visible,
    .btn-secondary:focus-visible {
      outline: none;
      box-shadow: 0 0 0 2px var(--border-color-focus);
    }

    /* --- Correções de Biblioteca e Layout --- */
    /* Garante que o container do mapa Leaflet não sobreponha outros elementos do modal. */
    .leaflet-container {
        z-index: 0;
    }

    /* Estilos para renderização de Markdown via Tailwind Typography. */
    .prose {
        --tw-prose-body: var(--text-primary);
        --tw-prose-headings: var(--text-primary);
        --tw-prose-lead: var(--text-secondary);
        --tw-prose-links: var(--text-primary);
        --tw-prose-bold: var(--text-primary);
        --tw-prose-counters: var(--text-secondary);
        --tw-prose-bullets: var(--border-color);
        --tw-prose-hr: var(--border-color);
        --tw-prose-quotes: var(--text-primary);
        --tw-prose-quote-borders: var(--border-color);
        --tw-prose-captions: var(--text-secondary);
        --tw-prose-code: var(--text-primary);
        --tw-prose-pre-code: var(--border-color);
        --tw-prose-pre-bg: var(--bg-surface);
        --tw-prose-th-borders: var(--border-color);
        --tw-prose-td-borders: var(--border-color);
        --tw-prose-invert-body: var(--text-tertiary);
        --tw-prose-invert-headings: var(--text-primary);
        --tw-prose-invert-lead: var(--text-secondary);
        --tw-prose-invert-links: var(--text-primary);
        --tw-prose-invert-bold: var(--text-primary);
        --tw-prose-invert-counters: var(--text-secondary);
        --tw-prose-invert-bullets: var(--text-secondary);
        --tw-prose-invert-hr: var(--text-secondary);
        --tw-prose-invert-quotes: var(--text-primary);
        --tw-prose-invert-quote-borders: var(--text-secondary);
        --tw-prose-invert-captions: var(--text-secondary);
        --tw-prose-invert-code: var(--text-primary);
        --tw-prose-invert-pre-code: var(--text-tertiary);
        --tw-prose-invert-pre-bg: rgb(0 0 0 / 50%);
        --tw-prose-invert-th-borders: var(--text-secondary);
        --tw-prose-invert-td-borders: var(--text-secondary);
    }
    .propercase::first-letter {
      text-transform: uppercase;
    }
  `}</style>
);

// Exporta o componente para que possa ser importado em outros lugares da aplicação.
export default GlobalStyles;
